import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { startOfDay } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { DateRangePickerNew } from '../../../../components/internal/DateRangePickerNew/index.js';
import { DurationPicker } from '../../../../components/internal/DurationPicker/index.js';
import { fallbackDuration, getDaysInRange, normalizeDate } from '../../../../components/SearchForm/components/TravelPeriodField/utils.js';
import { Container } from '../../../../components/SearchForm/components/TravelPeriodWidgetNext/components/Container.js';
import { Content } from '../../../../components/SearchForm/components/TravelPeriodWidgetNext/components/Content.js';
import { FooterElement } from '../../../../components/SearchForm/components/TravelPeriodWidgetNext/components/FooterElement/FooterElement.js';
import { HeadElement } from '../../../../components/SearchForm/components/TravelPeriodWidgetNext/components/HeadElement/HeadElement.js';
import { Wrapper } from '../../../../components/SearchForm/components/TravelPeriodWidgetNext/components/Wrapper.js';
import { useInputTypeState } from '../../../../components/SearchForm/components/TravelPeriodWidgetNext/hooks/useInputTypeState.js';
import { WidgetOverlay } from '../../../../components/SearchForm/components/WidgetOverlay/index.js';
import { qaId } from '../../../../utils/index.js';
import { SEARCH_FORM_QA_ID } from '../../../../utils/qaId.js';
import { SPACING_NONE } from '../../../../utils/spacingLegacy.js';
export const TravelPeriodWidgetNext = forwardRef(({
  type,
  open,
  travelPeriod,
  handlers: {
    onDurationChange,
    onRangeChange,
    onApply,
    onClose
  },
  config: {
    minDate,
    maxDate,
    durationOptionsNew,
    showHolidays,
    defaultDuration,
    holidayDates
  },
  layout,
  dimming = false,
  ...restProps
}, ref) => {
  const {
    t
  } = useTranslation();
  const {
    startDate,
    endDate
  } = travelPeriod || {};
  const [selectedState, setSelectedState] = useState(null);
  const startDateParsed = useMemo(() => startDate ? startOfDay(new Date(startDate)) : null, [startDate]);
  const endDateParsed = useMemo(() => endDate ? new Date(endDate) : null, [endDate]);
  const minDateParsed = useMemo(() => new Date(minDate), [minDate]);
  const maxDateParsed = useMemo(() => new Date(maxDate), [maxDate]);
  const [isDurationView, setIsDurationView] = useState(false);
  const [isDatePickerApplied, setIsDatePickerApplied] = useState(false);
  const [isDurationValid, setIsDurationValid] = useState(true);
  const isPopover = type === 'popover';
  const isDrawer = type === 'drawer';
  const days = useMemo(() => getDaysInRange(travelPeriod), [travelPeriod]);
  const duration = useMemo(() => travelPeriod.duration ?? fallbackDuration(days, defaultDuration), [days, travelPeriod, defaultDuration]);
  const [inputType, setInputType] = useInputTypeState(duration, durationOptionsNew, travelPeriod.duration);
  const handleRangeChange = range => {
    if (range.pending) {
      return false;
    }
    const startDate = normalizeDate(range?.start);
    const endDate = normalizeDate(range?.end);
    const days = getDaysInRange({
      startDate,
      endDate
    });
    const RangeDuration = days <= duration.min ? {
      min: 0,
      max: 0
    } : duration;
    onRangeChange({
      duration: RangeDuration,
      startDate,
      endDate
    });
  };
  const handleDurationChange = duration => {
    onDurationChange(duration);
  };
  const handleApply = useCallback(() => {
    setIsDatePickerApplied(true);
    onApply(selectedState, inputType);
  }, [setIsDatePickerApplied, selectedState, onApply, inputType]);
  const handleApplyClick = useCallback(() => {
    return isDurationView ? handleApply() : setIsDurationView(!isDurationView);
  }, [isDurationView, handleApply]);
  const handleSwitchView = type => () => {
    if (type === 'duration') {
      setIsDurationView(true);
      return;
    }
    setIsDurationView(false);
  };
  const head = _jsx(HeadElement, {
    range: travelPeriod,
    duration: duration,
    isDurationView: isDurationView,
    handleSwitchView: handleSwitchView,
    defaultDuration: defaultDuration,
    isDatePickerApplied: isDatePickerApplied,
    isInput: inputType === 'input'
  });
  return _jsx(WidgetOverlay, {
    type: type,
    open: open,
    dim: dimming || layout === 'compact' || layout === 'sidebar',
    children: _jsx(Container, {
      ...restProps,
      ref: ref,
      removePadding: true,
      type: type,
      title: t('travelPeriodWidgetNew:title.dialog'),
      onClose: onClose,
      topControls: isDrawer && head,
      "data-testid": "travel-period-widget",
      controls: _jsx(FooterElement, {
        selectedState: selectedState,
        setSelectedState: setSelectedState,
        showHolidays: showHolidays,
        onClick: handleApplyClick,
        disabled: !isDurationValid,
        isDurationView: isDurationView
      }),
      children: _jsxs(Wrapper, {
        "data-testid": qaId(SEARCH_FORM_QA_ID, 'travelPeriodLayer'),
        spacing: "none",
        children: [!isDrawer && head, _jsxs(Content, {
          direction: 'row',
          spacing: SPACING_NONE,
          children: [!isDurationView && _jsx(DateRangePickerNew, {
            holidayDates: holidayDates,
            selectedState: selectedState,
            showHolidays: showHolidays,
            orientation: isPopover ? 'horizontal' : 'vertical',
            startDate: startDateParsed,
            endDate: endDateParsed,
            minDate: minDateParsed,
            maxDate: maxDateParsed,
            onChange: handleRangeChange,
            alignTopControls: true
          }), isDurationView && _jsx(DurationPicker, {
            activeInputType: inputType,
            setActiveInputType: setInputType,
            range: travelPeriod,
            duration: duration,
            days: days,
            durationOptions: durationOptionsNew,
            onClickDateRange: handleSwitchView('picker'),
            onChange: handleDurationChange,
            onDurationIsValid: setIsDurationValid
          })]
        })]
      })
    })
  });
});